import { useState, useEffect } from 'react';
import { useAuth, getAuthToken } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/constants';
import {
  CogIcon,
  KeyIcon,
  SparklesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface AISettings {
  geminiApiKey: string;
  enabled: boolean;
  model: string;
  maxTokens: number;
}

const AdminSettings = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [testingAPI, setTestingAPI] = useState(false);

  const [settings, setSettings] = useState<AISettings>({
    geminiApiKey: '',
    enabled: false,
    model: 'gemini-2.0-flash',
    maxTokens: 2048,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchSettings();
  }, [isAuthenticated, navigate]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await fetch(`${API_ENDPOINTS.settings}/ai`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Merge with defaults to ensure all fields exist
          setSettings(prev => ({
            ...prev,
            ...result.data,
            geminiApiKey: result.data.geminiApiKey || '',
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch AI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const token = getAuthToken();
      const response = await fetch(`${API_ENDPOINTS.settings}/ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(settings),
      });

      const result = await response.json();

      if (result.success) {
        setMessage({
          type: 'success',
          text: 'AI settings saved successfully!',
        });
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Failed to save settings',
        });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save AI settings' });
    } finally {
      setSaving(false);
    }
  };

  const testAPIKey = async () => {
    const apiKey = settings.geminiApiKey?.trim();

    if (!apiKey) {
      setMessage({ type: 'error', text: 'Please enter an API key first' });
      return;
    }

    if (testingAPI) return; // Prevent double-clicks

    try {
      setTestingAPI(true);
      setMessage(null);

      const token = getAuthToken();
      const response = await fetch(`${API_ENDPOINTS.ai}/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          apiKey: apiKey,
          model: settings.model,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setMessage({
          type: 'success',
          text: '✅ API key is working! AI integration ready.',
        });
      } else {
        setMessage({
          type: 'error',
          text:
            result.error ||
            'API key test failed. Check your key and try again.',
        });
      }
    } catch (error) {
      console.error('API test error:', error);
      setMessage({
        type: 'error',
        text: 'Failed to connect to server. Make sure the server is running.',
      });
    } finally {
      setTestingAPI(false);
    }
  };

  const handleInputChange = (
    field: keyof AISettings,
    value: string | boolean | number
  ) => {
    setSettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CogIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">AI Settings</h1>
              <p className="text-gray-600">
                Configure AI-powered content generation
              </p>
            </div>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
            )}
            <span
              className={
                message.type === 'success' ? 'text-green-800' : 'text-red-800'
              }
            >
              {message.text}
            </span>
          </div>
        )}

        {/* Settings Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-6">
              <SparklesIcon className="h-6 w-6 text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                Gemini AI Configuration
              </h2>
            </div>

            <div className="space-y-6">
              {/* Enable AI */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-900">
                    Enable AI Content Generation
                  </label>
                  <p className="text-sm text-gray-600">
                    Turn on AI-powered writing assistance
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    handleInputChange('enabled', !settings.enabled)
                  }
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${
                    settings.enabled ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      settings.enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* API Key */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  <KeyIcon className="inline h-4 w-4 mr-1" />
                  Gemini API Key
                </label>
                <div className="flex space-x-3">
                  <input
                    type="password"
                    value={settings.geminiApiKey}
                    onChange={e =>
                      handleInputChange('geminiApiKey', e.target.value)
                    }
                    placeholder="Enter your Gemini API key..."
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                  />
                  <button
                    type="button"
                    onClick={testAPIKey}
                    disabled={testingAPI || !settings.geminiApiKey.trim()}
                    className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {testingAPI ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      'Test'
                    )}
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Get your free API key from{' '}
                  <a
                    href="https://aistudio.google.com/app/apikey"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Google AI Studio
                  </a>
                </p>
              </div>

              {/* Model Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  AI Model
                </label>
                <select
                  value={settings.model}
                  onChange={e => handleInputChange('model', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                >
                  <option value="gemini-2.0-flash">
                    Gemini 2.0 Flash (Recommended)
                  </option>
                  <option value="gemini-1.5-flash">
                    Gemini 1.5 Flash (Fast)
                  </option>
                  <option value="gemini-1.5-pro">
                    Gemini 1.5 Pro (Advanced)
                  </option>
                </select>
              </div>

              {/* Max Tokens */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Max Tokens ({settings.maxTokens})
                </label>
                <input
                  type="range"
                  min="500"
                  max="4096"
                  step="256"
                  value={settings.maxTokens}
                  onChange={e =>
                    handleInputChange('maxTokens', parseInt(e.target.value))
                  }
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-sm text-gray-600 mt-1">
                  <span>500 (Short)</span>
                  <span>2048 (Medium)</span>
                  <span>4096 (Long)</span>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={saveSettings}
                disabled={saving}
                className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-5 w-5" />
                    <span>Save AI Settings</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">
            🚀 AI Features Coming Soon
          </h3>
          <div className="space-y-2 text-blue-800">
            <p>• ✨ Auto-generate blog post content from topics</p>
            <p>• 📝 AI writing assistant in the post editor</p>
            <p>• 🎯 SEO-optimized content suggestions</p>
            <p>• 🌍 Travel destination research and facts</p>
            <p>• 📸 Image caption generation</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
